<script setup lang="ts">
/**
 * The whole price ladder on one screen: free, a single clean PDF, and the
 * 30-day pack. Card shape, spacing and check marks follow the template's
 * pricing block; the full comparison and FAQ live on /pricing.
 */
const { t } = useI18n()
const { isAuthenticated } = useAuthUser()
const { exportCreditPrice, jobPackPrice, yearlyPrice, price } = usePremiumPlans()

// Paid plans go to the premium page for members and to sign-up for visitors;
// the checkout itself needs an account to attach the purchase to.
const paidTarget = computed(() => (isAuthenticated.value ? '/admin/premium' : '/registration'))

// The pack is always listed: this is the price list, not the checkout. Whether
// it can be bought yet depends on the LemonSqueezy variant being configured.
const tiers = computed(() => [
  {
    id: 'free',
    name: t('homepage.pricingTeaser.free.name'),
    price: price(0),
    note: t('homepage.pricingTeaser.free.note'),
    features: [
      t('homepage.pricingTeaser.free.f1'),
      t('homepage.pricingTeaser.free.f2'),
      t('homepage.pricingTeaser.free.f3'),
      t('homepage.pricingTeaser.free.f4'),
    ],
    cta: t('homepage.pricingTeaser.free.cta'),
    icon: '/sofax/pricing-icon-1.svg',
    to: '/free-cv-builder',
    highlight: false,
  },
  {
    id: 'single',
    name: t('homepage.pricingTeaser.single.name'),
    price: exportCreditPrice,
    note: t('homepage.pricingTeaser.single.note'),
    features: [
      t('homepage.pricingTeaser.single.f1'),
      t('homepage.pricingTeaser.single.f2'),
      t('homepage.pricingTeaser.single.f3'),
    ],
    cta: t('homepage.pricingTeaser.single.cta'),
    icon: '/sofax/pricing-icon-3.svg',
    to: paidTarget.value,
    highlight: false,
  },
  {
    id: 'pack',
    name: t('premium.jobPack.title'),
    price: jobPackPrice,
    note: t('homepage.pricingTeaser.pack.note'),
    features: [
      t('premium.jobPack.stack.lines.cvs.title'),
      t('premium.jobPack.stack.lines.checks.title'),
      t('premium.jobPack.stack.lines.writing.title'),
      t('premium.jobPack.stack.lines.web.title'),
    ],
    cta: t('homepage.pricingTeaser.pack.cta'),
    icon: '/sofax/pricing-icon-2.svg',
    to: paidTarget.value,
    highlight: true,
  },
])
</script>

<template>
  <section id="pricing" class="sofax bg-white dark:bg-gray-950">
    <div class="section-space">
      <div class="shell">
        <div class="mx-auto mb-14 max-w-2xl text-center">
          <h2 class="mb-5">{{ t('homepage.pricingTeaser.title') }}</h2>
          <p class="text-lg">{{ t('homepage.pricingTeaser.subtitle') }}</p>
        </div>

        <ul class="grid grid-cols-1 gap-6 md:grid-cols-3">
          <li
            v-for="tier in tiers"
            :key="tier.id"
            class="relative flex flex-col rounded-[0.625rem] border bg-white p-[1.875rem] transition-all duration-300 hover:shadow-lift dark:bg-gray-800"
            :class="tier.highlight ? 'border-ink dark:border-white' : 'border-line dark:border-gray-700'"
          >
            <!-- Badge rides in the corner so every card's icon starts at the same height -->
            <span
              v-if="tier.highlight"
              class="absolute right-[1.875rem] top-[1.875rem] rounded-full bg-lilac px-3 py-1 text-xs font-bold uppercase tracking-wide text-ink"
            >
              {{ t('premium.recommended') }}
            </span>

            <!-- Template's pricing icon on a light tile (its art is dark) -->
            <span class="mb-5 inline-flex size-16 items-center justify-center rounded-[0.625rem] bg-surface">
              <img :src="tier.icon" alt="" aria-hidden="true" width="80" height="80" class="size-10" loading="lazy">
            </span>

            <div class="mb-5 font-title text-2xl font-bold -tracking-[0.5px] text-ink dark:text-white">
              {{ tier.name }}
            </div>

            <p class="mb-6 text-base">{{ tier.note }}</p>

            <div class="mb-8 font-title text-[2.75rem] font-bold leading-none text-ink dark:text-white">
              {{ tier.price }}
            </div>

            <ul class="mb-10 space-y-4">
              <li v-for="feature in tier.features" :key="feature" class="flex items-start gap-3">
                <i class="bi bi-check-circle-fill mt-1 shrink-0 text-accent" />
                <span class="text-base text-body dark:text-gray-300">{{ feature }}</span>
              </li>
            </ul>

            <div class="mt-auto">
              <SofaxBtn :to="tier.to" :label="tier.cta" :variant="tier.highlight ? 'accent' : 'outline'" block />
            </div>
          </li>
        </ul>

        <p class="mt-10 text-center text-base">
          {{ t('homepage.pricingTeaser.yearlyNote', { price: yearlyPrice }) }}
          <LocaleLink to="/pricing" class="font-semibold text-ink underline dark:text-white">
            {{ t('homepage.pricingTeaser.compare') }}
          </LocaleLink>
        </p>
      </div>
    </div>
  </section>
</template>
